import React from "react";
import styles from "./ConfigurableBundleField.module.css";
import type { BundleStyles } from "./FixedBundleField";

// ─── Types ────────────────────────────────────────────────────────────────────
// Defined locally so @codway-dev/ui stays free of @codway-dev/shared dependencies.
// The shared package exports structurally-compatible types that consumers spread
// into these props.

export interface ConfigurableBundleOption {
  id: string;
  variantId: string;
  productTitle: string;
  imageUrl?: string;
}

export interface ConfigurableBundleGroup {
  id: string;
  name: string;
  pickCount: number;
  options: ConfigurableBundleOption[];
}

export interface ConfigurableBundleFieldProps {
  groups: ConfigurableBundleGroup[];
  title?: string;
  description?: string;
  imageUrl?: string;
  totalPriceLabel: string;
  compareAtLabel?: string;
  savingsLabel?: string;
  styles?: BundleStyles;

  // Controlled selections keyed by group id → array of selected option ids.
  // Tracked by `option.id` (not `variantId`) so duplicate-variant options
  // within a group toggle independently — the admin can configure two
  // options pointing at the same variant by mistake, and the cards must
  // stay independent rather than ghost-toggling each other.
  selections: Record<string, string[]>;
  // Fires once per click with the full next-selections array for that
  // group. Single-event API (rather than per-toggle add/remove) keeps the
  // radio replace-and-pick transition atomic — consumers just assign
  // `nextSelections` and don't have to reconcile two events in a row.
  onSelectionChange: (groupId: string, nextSelections: string[]) => void;

  // Controlled view — parent decides whether the shopper sees the picker
  // (building/editing) or the summary (already committed to cart). Keeps
  // the component free of cart/lifecycle knowledge: the parent knows
  // what's in the cart and whether the shopper has tapped Edit.
  mode: "picker" | "summary";
  onAddToCart: () => void;
  // Fires when the summary view's Edit button is clicked. The parent is
  // expected to flip `mode` back to "picker" — the component does not
  // change its own mode.
  onEdit: () => void;
  onRemove: () => void;
  disabled?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function bundleCssVars(s?: BundleStyles): React.CSSProperties {
  if (!s) return {};
  return {
    "--bun-background-color": s.backgroundColor,
    "--bun-border-color": s.borderColor,
    "--bun-active-border-color": s.activeBorderColor,
    "--bun-border-style": s.borderStyle,
    "--bun-border-width":
      s.borderWidth != null ? `${s.borderWidth}px` : undefined,
    "--bun-border-radius":
      s.borderRadius != null ? `${s.borderRadius}px` : undefined,
  } as React.CSSProperties;
}

function isGroupSatisfied(
  group: ConfigurableBundleGroup,
  selections: Record<string, string[]>
): boolean {
  return (selections[group.id]?.length ?? 0) === group.pickCount;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ConfigurableBundleField: React.FC<
  ConfigurableBundleFieldProps
> = ({
  groups,
  title,
  description,
  imageUrl,
  totalPriceLabel,
  compareAtLabel,
  savingsLabel,
  styles: bundleStyles,
  selections,
  onSelectionChange,
  mode,
  onAddToCart,
  onEdit,
  onRemove,
  disabled = false,
}) => {
  const allSatisfied = groups.every((g) => isGroupSatisfied(g, selections));

  const handleOptionClick = (
    group: ConfigurableBundleGroup,
    option: ConfigurableBundleOption
  ) => {
    if (disabled) return;
    const current = selections[group.id] ?? [];
    const isPicked = current.includes(option.id);
    const isRadio = group.pickCount === 1;

    if (isPicked) {
      // Radio groups don't allow deselecting via click — the shopper
      // must pick a different option. This avoids accidental empty-radio
      // states where the group becomes unsatisfied (and the CTA disables)
      // after a stray click on the already-selected card.
      if (isRadio) return;
      onSelectionChange(
        group.id,
        current.filter((id) => id !== option.id)
      );
      return;
    }

    if (isRadio) {
      // Radio behavior — single replacement, emitted as one event so the
      // consumer doesn't see a transient empty selection between two
      // back-to-back updates.
      onSelectionChange(group.id, [option.id]);
      return;
    }

    // Checkbox behavior — block silently when at max. The group counter
    // and disabled CTA tell the shopper they need to uncheck first.
    if (current.length >= group.pickCount) return;
    onSelectionChange(group.id, [...current, option.id]);
  };

  const handleAddToCart = () => {
    if (disabled || !allSatisfied) return;
    onAddToCart();
  };

  const handleEdit = () => {
    if (disabled) return;
    onEdit();
  };

  const handleRemove = () => {
    if (disabled) return;
    onRemove();
  };

  // ── Summary (already in cart) ───────────────────────────────────────────────
  if (mode === "summary") {
    return (
      <div
        className={styles.card}
        style={bundleCssVars(bundleStyles)}
        data-disabled={disabled || undefined}
      >
        <div className={styles.summaryHeader}>
          {title && <span className={styles.title}>{title}</span>}
          <span className={styles.summaryCheck}>
            <span aria-hidden="true">✓</span> Bundle agregado a tu pedido
          </span>
        </div>

        <ul className={styles.summaryList}>
          {groups.map((group) => {
            const picked = selections[group.id] ?? [];
            const labels = picked
              .map((oid) => {
                const opt = group.options.find((o) => o.id === oid);
                return opt?.productTitle ?? "";
              })
              .filter(Boolean);
            return (
              <li key={group.id} className={styles.summaryRow}>
                <span className={styles.summaryGroupName}>{group.name}:</span>
                <span className={styles.summaryGroupPicks}>
                  {labels.join(", ")}
                </span>
              </li>
            );
          })}
        </ul>

        <div className={styles.summaryActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleEdit}
            disabled={disabled}
          >
            Editar
          </button>
          <button
            type="button"
            className={styles.removeButton}
            onClick={handleRemove}
            disabled={disabled}
          >
            Quitar
          </button>
        </div>
      </div>
    );
  }

  // ── Picker (not in cart, or in edit mode) ───────────────────────────────────
  return (
    <div
      className={styles.card}
      style={bundleCssVars(bundleStyles)}
      data-disabled={disabled || undefined}
    >
      {(imageUrl || title || description) && (
        <div className={styles.header}>
          {imageUrl && (
            <img
              src={imageUrl}
              alt=""
              aria-hidden="true"
              className={styles.coverImage}
            />
          )}
          {title && <span className={styles.title}>{title}</span>}
          {description && (
            <span className={styles.description}>{description}</span>
          )}
        </div>
      )}

      {groups.map((group) => {
        const picked = selections[group.id] ?? [];
        const isRadio = group.pickCount === 1;
        return (
          <div
            key={group.id}
            className={styles.group}
            role={isRadio ? "radiogroup" : "group"}
            aria-label={group.name}
          >
            <div className={styles.groupHeader}>
              <span className={styles.groupName}>{group.name}</span>
              <span className={styles.groupCounter}>
                Elige {group.pickCount}
                {group.pickCount > 1 && (
                  <span className={styles.groupCounterDetail}>
                    {" "}
                    ({picked.length}/{group.pickCount} seleccionados)
                  </span>
                )}
              </span>
            </div>

            <ul className={styles.optionList}>
              {group.options.map((option) => {
                const isPicked = picked.includes(option.id);
                return (
                  <li key={option.id}>
                    <div
                      role={isRadio ? "radio" : "checkbox"}
                      aria-checked={isPicked}
                      aria-disabled={disabled || undefined}
                      tabIndex={disabled ? -1 : 0}
                      className={`${styles.option} ${
                        isPicked ? styles.optionSelected : ""
                      }`}
                      onClick={() => handleOptionClick(group, option)}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          handleOptionClick(group, option);
                        }
                      }}
                    >
                      {isRadio ? (
                        <div
                          className={`${styles.radio} ${
                            isPicked ? styles.radioSelected : ""
                          }`}
                        >
                          <span
                            className={styles.radioInner}
                            aria-hidden="true"
                          />
                        </div>
                      ) : (
                        <div
                          className={`${styles.checkbox} ${
                            isPicked ? styles.checkboxSelected : ""
                          }`}
                          aria-hidden="true"
                        >
                          <span className={styles.checkboxMark}>✓</span>
                        </div>
                      )}

                      {option.imageUrl ? (
                        <img
                          src={option.imageUrl}
                          alt=""
                          aria-hidden="true"
                          className={styles.optionImage}
                        />
                      ) : (
                        <div
                          className={styles.optionImagePlaceholder}
                          aria-hidden="true"
                        />
                      )}

                      <span className={styles.optionTitle}>
                        {option.productTitle}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      <div className={styles.footer}>
        <div className={styles.priceRow}>
          {compareAtLabel && (
            <span className={styles.compareAt}>{compareAtLabel}</span>
          )}
          <span className={styles.total}>{totalPriceLabel}</span>
          {savingsLabel && (
            <span className={styles.savingsBadge}>Ahorra {savingsLabel}</span>
          )}
        </div>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleAddToCart}
          disabled={disabled || !allSatisfied}
        >
          Agregar bundle al carrito
        </button>
      </div>
    </div>
  );
};
